/** Darken a 6-digit hex toward black by `ratio` (0..1). Returns lowercase #rrggbb. */
export function darken(hex: string, ratio: number): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex)
  if (!m) return hex
  const n = parseInt(m[1] ?? '', 16)
  const r = (n >> 16) & 0xff
  const g = (n >> 8) & 0xff
  const b = n & 0xff
  const f = (c: number) => Math.max(0, Math.min(255, Math.round(c * (1 - ratio))))
  const out = (f(r) << 16) | (f(g) << 8) | f(b)
  return `#${out.toString(16).padStart(6, '0')}`
}

/** Editable theme tokens, in display order. Mirrors ALLOWED_THEME_KEYS in
 *  services/api/src/domain/org_config.py. */
export const THEME_KEYS = [
  'primary',
  'secondary',
  'accent',
  'success',
  'warning',
  'danger',
  'info',
  'surface',
  'ink',
] as const

/** The representative CSS var to READ a token's current value from :root
 *  (for seeding the editor). `applyTheme` may set additional derived vars. */
export const THEME_TOKEN_READ_VAR: Record<string, string> = {
  primary: '--steel-blue',
  secondary: '--secondary',
  accent: '--accent',
  success: '--success',
  warning: '--warning',
  danger: '--danger',
  info: '--info',
  surface: '--surface',
  ink: '--space-cadet',
}

/**
 * Map a validated org_config theme (token-name -> #hex) onto CSS custom
 * properties. Overrides BASE palette tokens so the alias layer in
 * tokens.css (--primary: var(--steel-blue), etc.) and the ~132 components
 * that read base tokens directly all recolor. Keys absent from `theme`
 * are left untouched, so YTL defaults stand.
 */
export function applyTheme(
  theme: Record<string, string>,
  target: HTMLElement = document.documentElement,
): void {
  const set = (name: string, value: string) => target.style.setProperty(name, value)
  for (const [key, value] of Object.entries(theme)) {
    switch (key) {
      case 'primary':
        set('--steel-blue', value)
        set('--steel-blue-2', darken(value, 0.1))
        set('--steel-blue-3', darken(value, 0.22))
        break
      case 'accent':
        set('--accent', value)
        break
      case 'secondary':
        set('--secondary', value)
        break
      case 'success':
        set('--success', value)
        break
      case 'warning':
        set('--warning', value)
        break
      case 'danger':
        set('--danger', value)
        break
      case 'info':
        set('--info', value)
        break
      case 'surface':
        set('--surface', value)
        break
      case 'ink':
        set('--space-cadet', value)
        break
      default:
        // Unknown keys are rejected server-side; ignore defensively.
        break
    }
  }
}
